import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input", "preview", "filename", "remove", "progress", "hidden"
  ]
  
  static values = {
    previewTemplate: String,
    maxFileSize: { type: Number, default: 5 }, // Max file size in MB
    allowedTypes: String
  }
  
  static classes = [
    "dragover", "hidden", "invalid"
  ]

  connect() {
    console.log("File upload controller connected")
    
    // Set up events
    this.inputTarget.addEventListener("change", this.handleFileSelection.bind(this))
    
    // Set up drag and drop if preview area exists
    if (this.hasPreviewTarget) {
      this.previewTarget.addEventListener("dragover", this.handleDragOver.bind(this))
      this.previewTarget.addEventListener("dragleave", this.handleDragLeave.bind(this))
      this.previewTarget.addEventListener("drop", this.handleDrop.bind(this))
      
      // Show preview if file is already selected
      if (this.inputTarget.files && this.inputTarget.files.length > 0) {
        this.showPreview(this.inputTarget.files[0])
      }
    }
    
    // Check if removal button should be visible
    if (this.hasRemoveTarget && this.hasFilenameTarget) {
      this.updateRemoveButtonVisibility()
    }
  }
  
  // Handle file input change
  handleFileSelection(event) {
    if (this.inputTarget.files && this.inputTarget.files.length > 0) {
      const file = this.inputTarget.files[0]
      
      if (this.validateFile(file)) {
        this.processFile(file)
      } else {
        this.inputTarget.value = "" // Clear the invalid selection
        
        // Show error message if needed
        if (this.hasInvalidClass) {
          this.element.classList.add(this.invalidClass)
          
          // Remove error class after 3 seconds
          setTimeout(() => {
            this.element.classList.remove(this.invalidClass)
          }, 3000)
        }
      }
    } else {
      this.clearPreview()
    }
  }
  
  // Handle file drag over
  handleDragOver(event) {
    event.preventDefault()
    
    if (this.hasDragoverClass) {
      this.previewTarget.classList.add(this.dragoverClass)
    }
  }
  
  // Handle file drag leave
  handleDragLeave(event) {
    event.preventDefault()
    
    if (this.hasDragoverClass) {
      this.previewTarget.classList.remove(this.dragoverClass)
    }
  }
  
  // Handle file drop
  handleDrop(event) {
    event.preventDefault()
    
    if (this.hasDragoverClass) {
      this.previewTarget.classList.remove(this.dragoverClass)
    }
    
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0]
      
      if (this.validateFile(file)) {
        this.inputTarget.files = event.dataTransfer.files // Assign to input
        this.processFile(file)
      }
    }
  }
  
  // Remove current file
  remove(event) {
    event.preventDefault()
    
    this.inputTarget.value = ""
    this.clearPreview()
    
    // Update hidden field if it exists
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = ""
    }
    
    if (this.hasRemoveTarget) {
      this.updateRemoveButtonVisibility()
    }
  }
  
  // Process the selected file
  processFile(file) {
    // Display preview if available
    if (this.hasPreviewTarget) {
      this.showPreview(file)
    }
    
    // Show filename if we have that target
    if (this.hasFilenameTarget) {
      this.filenameTarget.textContent = file.name
    }
    
    // Update hidden field if it exists (for forms that need to track if the file was removed)
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = "1" // Indicate file presence
    }
    
    // Update remove button visibility
    if (this.hasRemoveTarget) {
      this.updateRemoveButtonVisibility()
    }
    
    // For direct upload (Active Storage):
    if (this.element.dataset.directUpload === "true") {
      this.uploadFileDirectly(file)
    }
  }
  
  // Show file preview (if supported)
  showPreview(file) {
    // Only handle images for preview
    if (file.type.match('image.*')) {
      const reader = new FileReader()
      
      reader.onload = (e) => {
        if (this.hasPreviewTemplate) {
          // Use template if provided
          const template = this.previewTemplateValue
            .replace(/{src}/g, e.target.result)
            .replace(/{filename}/g, file.name)
            .replace(/{filesize}/g, this.formatFileSize(file.size))
          
          this.previewTarget.innerHTML = template
        } else {
          // Default display for images
          const img = document.createElement('img')
          img.src = e.target.result
          img.classList.add('img-preview')
          img.style.maxWidth = '100%'
          img.style.maxHeight = '200px'
          
          this.previewTarget.innerHTML = ''
          this.previewTarget.appendChild(img)
        }
        
        // Show the preview area
        if (this.hasHiddenClass) {
          this.previewTarget.classList.remove(this.hiddenClass)
        }
      }
      
      reader.readAsDataURL(file)
    } else {
      // For non-image files, just show an icon or filename
      this.previewTarget.innerHTML = `
        <div class="file-icon">
          <i class="fas fa-file"></i>
          <span>${file.name}</span>
        </div>
      `
      
      // Show the preview area
      if (this.hasHiddenClass) {
        this.previewTarget.classList.remove(this.hiddenClass)
      }
    }
  }
  
  // Clear preview
  clearPreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ""
      
      // Hide the preview area if needed
      if (this.hasHiddenClass) {
        this.previewTarget.classList.add(this.hiddenClass)
      }
    }
    
    if (this.hasFilenameTarget) {
      this.filenameTarget.textContent = "No file selected"
    }
    
    // Update remove button visibility
    if (this.hasRemoveTarget) {
      this.updateRemoveButtonVisibility()
    }
  }
  
  // Validate file based on size and type
  validateFile(file) {
    // Check file size
    if (this.maxFileSizeValue > 0 && file.size > (this.maxFileSizeValue * 1024 * 1024)) {
      alert(`File size exceeds maximum allowed size of ${this.maxFileSizeValue}MB`)
      return false
    }
    
    // Check file type if restrictions are set
    if (this.allowedTypesValue && this.allowedTypesValue.length > 0) {
      const allowedTypes = this.allowedTypesValue.split(',').map(type => type.trim())
      const fileExtension = file.name.split('.').pop().toLowerCase()
      
      if (!allowedTypes.includes(fileExtension)) {
        alert(`File type .${fileExtension} is not allowed. Allowed types: ${this.allowedTypesValue}`)
        return false
      }
    }
    
    return true
  }
  
  // Update visibility of the remove button
  updateRemoveButtonVisibility() {
    if (this.hasRemoveTarget) {
      if (this.inputTarget.files && this.inputTarget.files.length > 0) {
        this.removeTarget.classList.remove(this.hiddenClass || "hidden")
      } else {
        this.removeTarget.classList.add(this.hiddenClass || "hidden")
      }
    }
  }
  
  // Format file size for display
  formatFileSize(bytes) {
    if (bytes < 1024) {
      return bytes + ' bytes'
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB'
    } else {
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    }
  }
  
  // Upload file directly to Active Storage
  uploadFileDirectly(file) {
    if (!this.hasProgressTarget) return
    
    // Create direct upload controller
    const upload = new DirectUpload(
      file,
      this.inputTarget.dataset.directUploadUrl,
      {
        directUploadWillStoreFileWithXHR: (xhr) => {
          xhr.upload.addEventListener("progress", (event) => {
            const progress = event.loaded / event.total * 100
            this.progressTarget.style.width = progress + "%"
          })
        }
      }
    )
    
    // Show progress bar
    this.progressTarget.style.width = "0%"
    this.progressTarget.parentElement.classList.remove(this.hiddenClass || "hidden")
    
    // Perform upload
    upload.create((error, blob) => {
      if (error) {
        // Handle the error
        console.error("Direct upload error:", error)
        
        // Hide progress bar
        this.progressTarget.parentElement.classList.add(this.hiddenClass || "hidden")
      } else {
        // Upload complete - update hidden input with blob ID
        if (this.hasHiddenTarget) {
          this.hiddenTarget.value = blob.signed_id
        }
        
        // Complete the progress bar
        this.progressTarget.style.width = "100%"
        
        // Hide progress bar after a short delay
        setTimeout(() => {
          this.progressTarget.parentElement.classList.add(this.hiddenClass || "hidden")
        }, 1000)
      }
    })
  }
}